/**
 * Admin · one call (route /admin/calls/:callId): what was asked, the recording,
 * the transcript, and the call's P&L (GET /admin/pnl/calls/:callId).
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { adminApi, CallDetail, PnlCall, TranscriptMessage } from './adminApi';
import { fmtDuration, lineLabel, llmLabel, Profit, yen } from './AdminPnl';
import { AnsweredByBadge, TranscriptMessages } from './AdminUserDetail';
import { fmtAdminDateTime } from './timeFormat';
import './Admin.css';

function errorMessage(e: unknown, fallback: string): string {
  return e instanceof Error ? e.message : fallback;
}

/** A section that loads on its own: null while loading, a string on failure. */
type Loaded<T> = T | string | null;

function Row({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="row">
      <span>
        {label}
        {note && <span className="admin-muted"> · {note}</span>}
      </span>
      <span>{value}</span>
    </div>
  );
}

function PnlSection({ c }: { c: PnlCall }) {
  const u = c.usage;
  const est = c.aiEstimated ? ' (est.)' : '';
  return (
    <div className="admin-section">
      <h2>P&amp;L</h2>
      <div className="admin-cards">
        <div className="admin-card">
          <div className="value">{yen(c.revenue)}</div>
          <div className="label">
            Revenue · {c.creditDeducted ? `credit from ${c.plan}` : c.reason || 'not charged'}
          </div>
        </div>
        <div className="admin-card">
          <div className="value">{yen(c.cost)}</div>
          <div className="label">Cost</div>
        </div>
        <div className="admin-card">
          <div className="value">
            <Profit value={c.profit} />
          </div>
          <div className="label">Profit</div>
        </div>
      </div>
      <div className="admin-breakdowns">
        <div className="admin-breakdown">
          <h3>Costs</h3>
          <Row
            label="Twilio"
            note={[lineLabel(c.lineType), c.carrier, c.twilioPerMinute !== null && `${yen(c.twilioPerMinute)}/min`]
              .filter(Boolean)
              .join(' · ')}
            value={c.twilio === null ? 'pending' : yen(c.twilio)}
          />
          <Row label={`LLM${est}`} note={llmLabel(u)} value={yen(c.llm)} />
          <Row label={`Deepgram${est}`} note={u?.sttModel || undefined} value={yen(c.stt)} />
          <Row label={`Cartesia${est}`} note={u?.ttsModel || undefined} value={yen(c.tts)} />
          <Row label="Stripe fee" value={yen(c.stripeFee)} />
        </div>
        {u && (
          <div className="admin-breakdown">
            <h3>
              Usage
              {u.source === 'transcript'
                ? ' (estimated from the transcript)'
                : u.source === 'logs'
                  ? ' (from the server logs)'
                  : ''}
            </h3>
            <Row label="LLM requests" value={String(u.llmRequests)} />
            <Row
              label="Input tokens"
              note={`${u.llmCachedInputTokens.toLocaleString()} cached`}
              value={u.llmInputTokens.toLocaleString()}
            />
            <Row label="Output tokens" value={u.llmOutputTokens.toLocaleString()} />
            <Row label="Transcribed" value={`${u.sttSeconds}s`} />
            <Row label="Synthesized" value={`${u.ttsCharacters.toLocaleString()} characters`} />
          </div>
        )}
      </div>
    </div>
  );
}

const LANGUAGES: Record<string, string> = { ja: 'Japanese', en: 'English' };
const SITES: Record<string, string> = { jp: 'callout.jp', us: 'heycallout.com' };

function AdminCallDetail() {
  const { callId = '' } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<Loaded<CallDetail>>(null);
  const [pnl, setPnl] = useState<Loaded<PnlCall>>(null);
  const [transcript, setTranscript] = useState<Loaded<TranscriptMessage[]>>(null);

  useEffect(() => {
    let cancelled = false;
    const set =
      <T,>(setter: (v: Loaded<T>) => void) =>
      (v: Loaded<T>) =>
        !cancelled && setter(v);
    setDetail(null);
    setPnl(null);
    setTranscript(null);
    adminApi
      .callDetail(callId)
      .then(set(setDetail))
      .catch((e) => set(setDetail)(errorMessage(e, 'Failed to load the call')));
    adminApi
      .callPnl(callId)
      .then((r) => set(setPnl)(r.call))
      .catch((e) => set(setPnl)(errorMessage(e, 'Failed to load the P&L')));
    return () => {
      cancelled = true;
    };
  }, [callId]);

  // The transcript endpoint 404s when there is none, so ask only when there is.
  const hasTranscript = typeof detail === 'object' && detail !== null && Boolean(detail.transcriptUrl);
  useEffect(() => {
    if (!hasTranscript) return;
    let cancelled = false;
    adminApi
      .transcript(callId)
      .then((r) => !cancelled && setTranscript(r.transcript))
      .catch((e) => !cancelled && setTranscript(errorMessage(e, 'Failed to load the transcript')));
    return () => {
      cancelled = true;
    };
  }, [callId, hasTranscript]);

  const d = typeof detail === 'object' ? detail : null;

  return (
    <div className="admin-container">
      <button className="admin-back" onClick={() => navigate(-1)}>
        ← Back
      </button>

      {typeof detail === 'string' && <div className="admin-error">{detail}</div>}
      {detail === null && <div className="admin-muted">Loading…</div>}

      {d && (
        <>
          <div className="admin-header" style={{ marginTop: 12 }}>
            <h1>
              Call to {d.toNumber} <span className={`admin-badge ${d.status || ''}`}>{d.status || 'unknown'}</span>{' '}
              <AnsweredByBadge answeredBy={d.answeredBy} />
            </h1>
          </div>
          <div className="admin-call-meta">
            <span>{fmtAdminDateTime(d.initiatedAt)}</span>
            <span>{d.duration != null ? fmtDuration(d.duration) : '—'}</span>
            {d.userId && (
              <span>
                <Link to={`/admin/users/${d.userId}`}>
                  {d.userName && d.userEmail ? `${d.userName} (${d.userEmail})` : d.userName || d.userEmail || d.userId}
                </Link>
              </span>
            )}
            <span>{LANGUAGES[d.language || ''] || 'language unknown'}</span>
            <span>to {d.toCountry || 'unknown country'}</span>
            <span>user's site: {SITES[d.userMarket || ''] || 'unknown'}</span>
            {d.source && <span>{d.source}</span>}
            {d.workflow && <span>{d.workflow}</span>}
            <span>credit {d.creditDeducted ? 'deducted' : `not deducted${d.reason ? ` (${d.reason})` : ''}`}</span>
            <span className="admin-muted">{d.callSid}</span>
          </div>
          {d.deletionStatus && (
            <div className="admin-notice" style={{ marginTop: 12 }}>
              Deleted by the user {fmtAdminDateTime(d.deletionRequestedAt)}.{' '}
              {d.deletionStatus === 'pending'
                ? `Hidden from them; the recording, transcript and prompt are erased automatically ${fmtAdminDateTime(d.purgeAfter)}.`
                : `Recording, transcript and prompt erased ${fmtAdminDateTime(d.contentPurgedAt)}${
                    d.contentPurgedBy === 'auto' ? ' automatically' : ` by ${d.contentPurgedBy || 'unknown'}`
                  }.`}{' '}
              <Link to="/admin?tab=deleted">All deleted calls</Link>
            </div>
          )}

          <div className="admin-section">
            <h2>Recording</h2>
            {d.audioUrl ? (
              <>
                <audio className="admin-audio" controls src={d.audioUrl} preload="metadata" aria-label="Call recording" />
                <div className="admin-links" style={{ marginTop: 8 }}>
                  <a href={d.audioUrl} target="_blank" rel="noopener noreferrer">
                    Download audio
                  </a>
                  {d.transcriptUrl && (
                    <a href={d.transcriptUrl} target="_blank" rel="noopener noreferrer">
                      Raw transcript JSON
                    </a>
                  )}
                </div>
              </>
            ) : (
              <div className="admin-muted">No recording</div>
            )}
          </div>

          <div className="admin-section">
            <h2>Transcript</h2>
            {!hasTranscript ? (
              <div className="admin-muted">No transcript</div>
            ) : transcript === null ? (
              <div className="admin-muted">Loading…</div>
            ) : typeof transcript === 'string' ? (
              <div className="admin-error">{transcript}</div>
            ) : (
              <TranscriptMessages messages={transcript} />
            )}
          </div>
        </>
      )}

      {typeof pnl === 'string' && <div className="admin-error">{pnl}</div>}
      {pnl && typeof pnl === 'object' && <PnlSection c={pnl} />}

      {d && (d.prompt || d.objective || d.context || d.attribution || d.landingPage) && (
        <div className="admin-section">
          <h2>Request</h2>
          {d.prompt && (
            <div className="admin-prompt">
              <strong>Prompt</strong>
              {'\n'}
              {d.prompt}
            </div>
          )}
          {(d.objective || d.context) && (
            <div className="admin-prompt">
              {d.objective && (
                <>
                  <strong>Objective</strong>
                  {'\n'}
                  {d.objective}
                  {'\n\n'}
                </>
              )}
              {d.context && (
                <>
                  <strong>Context</strong>
                  {'\n'}
                  {JSON.stringify(d.context, null, 2)}
                </>
              )}
            </div>
          )}
          {(d.attribution || d.landingPage) && (
            <div className="admin-call-meta" style={{ marginTop: 10 }}>
              {d.landingPage && <span>landing: {d.landingPage}</span>}
              {d.attribution?.utmSource && <span>utm_source: {d.attribution.utmSource}</span>}
              {d.attribution?.utmCampaign && <span>utm_campaign: {d.attribution.utmCampaign}</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AdminCallDetail;
